import { logger as consoleLogger } from '@/utils/logger';
/**
 * Logger Service
 * Provides environment-aware logging with different levels
 * Prevents sensitive data exposure in production
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  service?: string;
  method?: string;
  userId?: string;
  [key: string]: any;
}

type ErrorDetails = {
  name?: string;
  message?: string;
  stack?: string;
  code?: string;
  status?: number;
};

/**
 * Keep stable operational fields from Error subclasses such as PostgrestError.
 * This is deliberately narrow: arbitrary response bodies must not be copied
 * into telemetry, but SQLSTATE/status are needed to diagnose policy failures.
 */
export function extractErrorDetails(
  error: Error | unknown,
  includeStack: boolean,
): ErrorDetails | unknown {
  if (error instanceof Error) {
    const candidate = error as Error & { code?: unknown; status?: unknown };
    return {
      name: error.name,
      message: error.message,
      ...(includeStack && error.stack ? { stack: error.stack } : {}),
      ...(typeof candidate.code === 'string' ? { code: candidate.code } : {}),
      ...(typeof candidate.status === 'number' ? { status: candidate.status } : {}),
    };
  }
  return error;
}

class Logger {
  private static instance: Logger;
  private static reentry = 0;
  private get isDevelopment() {
    // Runtime-safe: import.meta.env is undefined in Node (server routes +
    // Workflow DevKit steps). Reading `.DEV` there threw "Cannot read
    // properties of undefined (reading 'DEV')" — and because this getter runs
    // inside Logger.error(), it crashed the very error-handling path of every
    // service call in a workflow (BUG-33 saveResult/finalizePlan).
    try {
      const env = (import.meta as unknown as { env?: { DEV?: boolean } }).env;
      if (env?.DEV !== undefined) return Boolean(env.DEV);
    } catch {
      /* import.meta unsupported */
    }
    return typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production';
  }
  private get isDebugEnabled() {
    try {
      const env = (import.meta as unknown as { env?: { VITE_DEBUG?: string } }).env;
      if (env?.VITE_DEBUG !== undefined) return env.VITE_DEBUG === 'true';
    } catch {
      /* import.meta unsupported */
    }
    return typeof process !== 'undefined' && process.env?.VITE_DEBUG === 'true';
  }
  private get logLevel(): LogLevel {
    return this.isDevelopment ? 'debug' : 'error';
  }

  private constructor() {}

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  /**
   * Debug level logging - only in development
   */
  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment && this.isDebugEnabled) {
      consoleLogger.debug(`[DEBUG] ${this.formatMessage(message, context)}`);
    }
  }

  /**
   * Info level logging - development only
   */
  info(message: string, context?: LogContext): void {
    if (this.isDevelopment) {
      consoleLogger.debug(`[INFO] ${this.formatMessage(message, context)}`);
    }
  }

  /**
   * Warning level logging
   */
  warn(message: string, context?: LogContext): void {
    const formatted = this.formatMessage(message, context);
    if (this.isDevelopment) {
      consoleLogger.warn(`[WARN] ${formatted}`);
    } else {
      // In production, send to error tracking service
      this.sendToErrorTracking('warning', formatted, context);
    }
  }

  /**
   * Error level logging - always logged
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (Logger.reentry > 2) {
      console.error('[Logger reentry guard tripped]', message);
      return;
    }
    Logger.reentry++;
    try {
      const formatted = this.formatMessage(message, context);
      const errorDetails = extractErrorDetails(error, this.isDevelopment);

      if (this.isDevelopment) {
        consoleLogger.error(`[ERROR] ${formatted}`, errorDetails);
      } else {
        // In production, send to error tracking service
        this.sendToErrorTracking('error', formatted, { ...context, error: errorDetails });
      }
    } finally {
      Logger.reentry--;
    }
  }

  /**
   * Log performance metrics
   */
  performance(operation: string, duration: number, context?: LogContext): void {
    if (this.isDevelopment) {
      consoleLogger.debug(`[PERF] ${operation} took ${duration}ms`, context);
    } else if (duration > 1000) {
      // Log slow operations in production
      this.sendToErrorTracking(
        'performance',
        `Slow operation: ${operation} (${duration}ms)`,
        context,
      );
    }
  }

  /**
   * Create a child logger with preset context
   */
  createServiceLogger(serviceName: string): ServiceLogger {
    return new ServiceLogger(serviceName, this);
  }

  private formatMessage(message: string, context?: LogContext): string {
    if (!context) return message;

    const { service, method, ...rest } = context;
    let formatted = message;

    if (service) formatted = `[${service}] ${formatted}`;
    if (method) formatted = `${formatted} (${method})`;

    // Filter out sensitive keys
    const safeContext = this.filterSensitiveData(rest);
    if (Object.keys(safeContext).length > 0) {
      formatted = `${formatted} ${JSON.stringify(safeContext)}`;
    }

    return formatted;
  }

  private filterSensitiveData(data: any): any {
    const sensitiveKeys = ['password', 'token', 'secret', 'key', 'authorization', 'cookie'];

    if (typeof data !== 'object' || data === null) return data;

    const filtered: any = Array.isArray(data) ? [] : {};

    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
        filtered[key] = '[REDACTED]';
      } else if (typeof value === 'object' && value !== null) {
        filtered[key] = this.filterSensitiveData(value);
      } else {
        filtered[key] = value;
      }
    }

    return filtered;
  }

  private sendToErrorTracking(level: string, message: string, context?: any): void {
    import('@sentry/react').then((Sentry) => {
      const levelMap = { warning: 'warning', error: 'error', performance: 'info' } as const;
      const error = context?.error;
      const errorCode =
        error && typeof error === 'object' && typeof error.code === 'string'
          ? error.code
          : undefined;
      const httpStatus =
        error && typeof error === 'object' && typeof error.status === 'number'
          ? String(error.status)
          : undefined;
      // Keep error events searchable without putting post content, URLs, or
      // arbitrary service payloads into Sentry. The allowed fields describe the
      // failing operation, never customer data.
      const observability = Object.fromEntries(
        Object.entries({
          service: context?.service,
          method: context?.method,
          operation: context?.operation,
          entrypoint: context?.entrypoint,
          table: context?.table,
          postgres_code: errorCode,
          http_status: httpStatus,
          account_scope: context?.accountScope,
        }).filter(([, value]) => typeof value === 'string' && value.length > 0),
      );
      Sentry.withScope((scope) => {
        if (context?.userId) scope.setUser({ id: context.userId });
        if (context?.feature) scope.setTag('feature', context.feature);
        if (context?.page) scope.setTag('page', context.page);
        for (const [key, value] of Object.entries(observability)) {
          scope.setTag(key, value);
        }
        if (Object.keys(observability).length > 0) {
          scope.setContext('mora_log', observability);
        }
        Sentry.captureMessage(message, levelMap[level as keyof typeof levelMap] ?? 'info');
      });
    });
  }
}

/**
 * Service-specific logger with preset context
 */
export class ServiceLogger {
  constructor(
    private serviceName: string,
    private logger: Logger,
  ) {}

  debug(message: string, context?: LogContext): void {
    this.logger.debug(message, { service: this.serviceName, ...context });
  }

  info(message: string, context?: LogContext): void {
    this.logger.info(message, { service: this.serviceName, ...context });
  }

  warn(message: string, context?: LogContext): void {
    this.logger.warn(message, { service: this.serviceName, ...context });
  }

  error(message: string, error?: Error | unknown, context?: LogContext): void {
    this.logger.error(message, error, { service: this.serviceName, ...context });
  }

  performance(operation: string, duration: number, context?: LogContext): void {
    this.logger.performance(operation, duration, { service: this.serviceName, ...context });
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Export types
export type { LogLevel, LogContext };
